import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  Check,
  X,
  AlertCircle,
  Copy,
  Edit3,
  Trash2,
  HelpCircle,
  Calendar,
  Send,
} from "lucide-react";
import moment from "moment";

const SenderCard = ({
  sender,
  index,
  editingId,
  editValue,
  setEditValue,
  errors,
  setErrors,
  handleEditSenderId,
  cancelEditing,
  startEditing,
  copyToClipboard,
  openDeleteModal,
  getStatusColor,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  
  // Check if sender can be edited (approved and pending senders)
  const canEdit =
    sender.status?.toLowerCase() === "approved" ||
    sender.status?.toLowerCase() === "pending";

  // Check if sender can be deleted (rejected senders cannot be deleted)
  const canDelete = sender.status?.toLowerCase() !== "rejected";

  const handleEditClick = () => {
    if (canEdit) {
      setIsEditing(true);
      startEditing(sender);
    } else {
      toast.warning("Only approved and pending sender IDs can be edited.");
    }
  };

  const handleSaveClick = () => {
    if (editValue !== sender.sender) {
      handleEditSenderId(sender.sender_slug);
    }
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    cancelEditing();
  };

  const handleInputChange = (e) => {
    setEditValue(e.target.value);
    if (Object.keys(errors).length > 0) {
      setErrors({});
    }
  };

  const handleDeleteClick = () => {
    if (canDelete) {
      openDeleteModal(sender);
    } else {
      toast.error(
        "Rejected sender IDs cannot be deleted. Please contact support for assistance."
      );
    }
  };

  // Get card styling based on status
  const getCardStyling = (status) => {
    const statusLower = status?.toLowerCase();

    if (statusLower === "approved") {
      return {
        background: "bg-gradient-to-br from-green-50 to-green-60",
        border: "border-green-200",
        iconBg: "from-green-500 to-green-600",
        statusColor: "text-green-700",
        statusBg: "bg-green-100",
      };
    }
    if (statusLower === "pending") {
      return {
        background: "bg-gradient-to-br from-amber-50 to-yellow-50",
        border: "border-amber-200",
        iconBg: "from-amber-500 to-yellow-600",
        statusColor: "text-amber-700",
        statusBg: "bg-amber-100",
      };
    }
    if (statusLower === "rejected") {
      return {
        background: "bg-gradient-to-br from-red-50 to-rose-50",
        border: "border-red-200",
        iconBg: "from-red-500 to-rose-600",
        statusColor: "text-red-700",
        statusBg: "bg-red-100",
      };
    }
    return {
      background: "bg-gradient-to-br from-gray-50 to-slate-50",
      border: "border-gray-200",
      iconBg: "from-gray-400 to-slate-500",
      statusColor: "text-gray-700",
      statusBg: "bg-gray-100",
    };
  };

  const cardStyle = getCardStyling(sender.status);
  const isSaveDisabled = editValue === sender.sender || !editValue.trim();

  return (
    <motion.div
      key={sender.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.01 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      className={`${cardStyle.background} rounded-xl border ${cardStyle.border} p-4 hover:shadow-md transition-all duration-200 relative`}
    >
      {/* Header with actions */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 bg-gradient-to-br ${cardStyle.iconBg} rounded-lg flex items-center justify-center`}
          >
            <Send className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{sender.sender}</h3>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Calendar className="w-3 h-3" />
              <span>{moment(sender.created_at).format("MMM DD, YYYY")}</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-1">
          {isEditing && editingId === sender.sender_slug ? (
            <>
              <motion.button
                onClick={handleSaveClick}
                disabled={isSaveDisabled}
                className={`p-1.5 rounded ${
                  isSaveDisabled
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-green-600 hover:bg-green-50"
                }`}
                whileHover={!isSaveDisabled ? { scale: 1.1 } : {}}
                whileTap={!isSaveDisabled ? { scale: 0.95 } : {}}
              >
                <Check className="w-4 h-4" />
              </motion.button>
              <motion.button
                onClick={handleCancelClick}
                className="p-1.5 text-red-600 rounded hover:bg-red-50"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <X className="w-4 h-4" />
              </motion.button>
            </>
          ) : (
            <>
              <motion.button
                onClick={handleEditClick}
                disabled={!canEdit}
                className={`p-1.5 rounded ${
                  canEdit
                    ? "text-blue-600 hover:bg-blue-50"
                    : "text-gray-300 cursor-not-allowed"
                }`}
                whileHover={canEdit ? { scale: 1.1 } : {}}
                whileTap={canEdit ? { scale: 0.95 } : {}}
                title={
                  canEdit
                    ? "Edit sender ID"
                    : "Only approved and pending senders can be edited"
                }
              >
                <Edit3 className="w-4 h-4" />
              </motion.button>
              <motion.button
                onClick={handleDeleteClick}
                disabled={!canDelete}
                className={`p-1.5 rounded ${
                  canDelete
                    ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
                    : "text-gray-300 cursor-not-allowed"
                }`}
                whileHover={canDelete ? { scale: 1.1 } : {}}
                whileTap={canDelete ? { scale: 0.95 } : {}}
                title={
                  canDelete
                    ? "Delete sender ID"
                    : "Rejected senders cannot be deleted"
                }
              >
                <Trash2 className="w-4 h-4" />
              </motion.button>
            </>
          )}
        </div>
      </div>

      {/* Status badge */}
      <div className="flex justify-center mb-3">
        <div
          className={`px-3 py-1.5 rounded-full text-sm font-medium ${cardStyle.statusBg} ${cardStyle.statusColor}`}
        >
          {sender.status?.charAt(0).toUpperCase() + sender.status?.slice(1)}
        </div>
      </div>

      {/* Edit input (only shows when editing this specific card) */}
      {isEditing && editingId === sender.sender_slug && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-3 space-y-2"
        >
          <input
            type="text"
            value={editValue}
            onChange={handleInputChange}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={11}
            placeholder="Enter sender ID"
            autoFocus
          />
          
          {Object.keys(errors).length > 0 && (
            <div className="space-y-1">
              {Object.values(errors).map((error, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 p-2 text-xs text-red-600 rounded bg-red-50"
                >
                  <AlertCircle className="flex-shrink-0 w-3.5 h-3.5" />
                  <span>{error}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Status-specific messages */}
      {(sender.status?.toLowerCase() === "rejected" ||
        sender.status?.toLowerCase() === "pending") && (
        <div className="mt-3">
          {sender.status?.toLowerCase() === "rejected" && (
            <div className="p-2 rounded-lg bg-red-50 border border-red-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-red-700">
                  <HelpCircle className="w-3.5 h-3.5" />
                  <span className="truncate">{sender.reject_reason}</span>
                </div>
                <button
                  onClick={() => window.open("/contact-us", "_blank")}
                  className="px-2 py-0.5 text-xs text-white bg-red-600 rounded transition-colors hover:bg-red-700"
                >
                  Contact
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Copy button */}
      <div className="mt-4">
        <motion.button
          onClick={() => copyToClipboard(sender.sender)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 hover:bg-gray-50"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          title="Copy to clipboard"
        >
          <Copy className="w-4 h-4" />
          <span>Copy ID</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default SenderCard;